import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Zap, Users, Building2, BarChart3, Download, Plus, Trash2, Edit2, 
  ChevronRight, Trophy, Target, Clock, AlertTriangle, X, Check, Mail, Copy, Gamepad2,
  Eye, EyeOff, RotateCcw
} from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '../App';
import NotificationBell from '../components/NotificationBell';

const TeacherDashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [pupils, setPupils] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [school, setSchool] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  
  // Modals
  const [showAddClass, setShowAddClass] = useState(false);
  const [showAddPupil, setShowAddPupil] = useState(false);
  const [showChallenge, setShowChallenge] = useState(false);
  
  // Form data
  const [newClass, setNewClass] = useState({ name: '', year_group: 3 });
  const [newPupil, setNewPupil] = useState({ real_name: '', codename: '' });
  const [createdPupil, setCreatedPupil] = useState(null);
  const [revealedPw, setRevealedPw] = useState({});
  const [challengeData, setChallengeData] = useState({ class_a_id: '', class_b_id: '' });

  useEffect(() => {
    if (user?.user_type !== 'teacher') {
      navigate('/login');
      return;
    }
    fetchDashboard();
    fetchClasses();
    fetchSchool();
  }, [user]);

  const fetchSchool = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      const response = await axios.get(`${API}/teacher/school`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchool(response.data);
    } catch (err) {
      console.error('Failed to fetch school:', err);
    }
  };

  const copySchoolCode = () => {
    if (!school?.code) return;
    const code = school.code;
    const markCopied = () => {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    };
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(markCopied).catch(() => fallbackCopy(code, markCopied));
      } else {
        fallbackCopy(code, markCopied);
      }
    } catch {
      fallbackCopy(code, markCopied);
    }
  };

  const fallbackCopy = (text, onDone) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      onDone();
    } catch (e) {
      console.error('Copy failed:', e);
    }
  };

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      const response = await axios.get(`${API}/teacher/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      const response = await axios.get(`${API}/teacher/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data.classes || []);
    } catch (err) {
      console.error('Failed to fetch classes:', err);
    }
  };

  const fetchClassPupils = async (classId) => {
    try {
      const token = localStorage.getItem('typetown_token');
      const response = await axios.get(`${API}/teacher/classes/${classId}/pupils`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPupils(response.data);
      setSelectedClass(classes.find(c => c.id === classId));
    } catch (err) {
      console.error('Failed to fetch pupils:', err);
    }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.post(`${API}/teacher/classes`, newClass, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAddClass(false);
      setNewClass({ name: '', year_group: 3 });
      fetchClasses();
      fetchDashboard();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create class');
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!confirm('Are you sure you want to delete this class?')) return;
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.delete(`${API}/teacher/classes/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchClasses();
      fetchDashboard();
      if (selectedClass?.id === classId) {
        setSelectedClass(null);
        setPupils([]);
      }
    } catch (err) {
      alert('Failed to delete class');
    }
  };

  const handleAddPupil = async (e) => {
    e.preventDefault();
    if (!selectedClass) return;
    try {
      const token = localStorage.getItem('typetown_token');
      const res = await axios.post(`${API}/teacher/classes/${selectedClass.id}/pupils`, {
        real_name: newPupil.real_name,
        codename: newPupil.codename,
        class_id: selectedClass.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatedPupil(res.data);
      setNewPupil({ real_name: '', codename: '' });
      fetchClassPupils(selectedClass.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add pupil');
    }
  };

  const handleResetPassword = async (pupilId) => {
    if (!confirm('Generate a new password for this pupil?')) return;
    try {
      const token = localStorage.getItem('typetown_token');
      const res = await axios.post(`${API}/teacher/pupils/${pupilId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchClassPupils(selectedClass.id);
      setRevealedPw((prev) => ({ ...prev, [pupilId]: true }));
      alert(`New password: ${res.data.password}`);
    } catch (err) {
      alert('Failed to reset password');
    }
  };

  const handleEditCodename = async (pupil) => {
    const codename = prompt('Enter a new codename for this pupil:', pupil.username);
    if (!codename || codename.trim() === pupil.username) return;
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.put(`${API}/teacher/pupils/${pupil.id}`, { codename: codename.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchClassPupils(selectedClass.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update codename');
    }
  };

  const handleSetLevel = async (pupilId, stage) => {
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.put(`${API}/teacher/pupils/${pupilId}/level`, { stage, level: 1 }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchClassPupils(selectedClass.id);
      fetchDashboard();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update level');
    }
  };

  const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  const handlePrintLogins = () => {
    if (!selectedClass || pupils.length === 0) return;
    const code = escapeHtml(school?.code || '');
    const className = escapeHtml(selectedClass.name);
    const cards = pupils.map((p) => `
      <div class="card">
        <div class="pname">${escapeHtml(p.real_name || p.username)}</div>
        <div class="row"><span>School Code</span><b>${code}</b></div>
        <div class="row"><span>Username</span><b>${escapeHtml(p.username)}</b></div>
        <div class="row"><span>Password</span><b>${escapeHtml(p.plain_password || '')}</b></div>
      </div>`).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>TypeTown Logins - ${className}</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:24px;}
        h1{font-size:22px;margin:0 0 4px;} .sub{color:#555;margin:0 0 16px;font-size:13px;}
        .steps{background:#f4faf9;border:1px solid #4ECDC4;border-radius:8px;padding:12px 16px;margin-bottom:20px;font-size:13px;}
        .steps b{color:#0a8f82;}
        .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
        .card{border:2px solid #222;border-radius:10px;padding:12px 14px;page-break-inside:avoid;}
        .pname{font-size:18px;font-weight:bold;margin-bottom:8px;}
        .row{display:flex;justify-content:space-between;font-size:14px;padding:3px 0;border-top:1px dashed #ccc;}
        .row span{color:#666;} .row b{font-family:'Courier New',monospace;}
        @media print{.noprint{display:none;}}
      </style></head><body>
      <h1>TypeTown Logins - ${className}</h1>
      <p class="sub">Keep these safe. Cut out each card and hand it to the right child or parent.</p>
      <div class="steps">
        <b>How to log in:</b> 1. Go to <b>typetown.co.uk</b> &nbsp; 2. Click <b>Login</b> and choose <b>School / Pupil</b> &nbsp;
        3. Enter your <b>School Code</b>, <b>Username</b> and <b>Password</b> shown on your card. &nbsp; 4. Start typing and have fun!
      </div>
      <div class="grid">${cards}</div>
      <button class="noprint" onclick="window.print()" style="margin-top:20px;padding:10px 16px;font-size:14px;cursor:pointer;">Print</button>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) { alert('Please allow pop-ups to print pupil logins.'); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (e) {} }, 500);
  };

  const handleDeletePupil = async (pupilId) => {
    if (!confirm('Are you sure you want to remove this pupil?')) return;
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.delete(`${API}/teacher/pupils/${pupilId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchClassPupils(selectedClass.id);
    } catch (err) {
      alert('Failed to remove pupil');
    }
  };

  const handleCreateChallenge = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('typetown_token');
      await axios.post(`${API}/challenges/class`, challengeData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowChallenge(false);
      setChallengeData({ class_a_id: '', class_b_id: '' });
      fetchDashboard();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create challenge');
    }
  };

  const handleExportSENCO = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      const response = await axios.get(`${API}/teacher/senco/export${selectedClass ? `?class_id=${selectedClass.id}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `senco_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('SENCO export failed:', err);
      alert('Failed to export report');
    }
  };

  const handleSendWeeklySummary = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      const response = await axios.post(`${API}/teacher/send-weekly-summary`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Weekly summary has been generated! Check your notifications.');
    } catch (err) {
      alert('Failed to generate weekly summary');
    }
  };

  const stageNames = ['SPARK', 'SCOUT', 'CODER', 'BUILDER', 'CHAMPION'];

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-space flex items-center justify-center">
        <div className="text-bot-cyan animate-pulse font-display text-2xl">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-space">
      <div className="grid-bg" />
      
      {/* Top Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-panel/90 backdrop-blur-md border-b border-mid-panel">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Zap className="w-6 h-6 text-zap-yellow" />
            <span className="font-display text-xl">
              <span className="text-bot-cyan">Type</span>
              <span className="text-zap-yellow">Town</span>
            </span>
            <span className="font-mono text-xs text-screen-white/50 ml-2">TEACHER</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <NotificationBell />
            <span className="font-body text-sm text-screen-white/70">{user?.username}</span>
            <button onClick={logout} className="text-screen-white/50 hover:text-glitch-red text-sm" data-testid="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </nav>
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-display text-3xl text-screen-white" data-testid="teacher-welcome">
                Teacher Dashboard
              </h1>
              <p className="font-body text-screen-white/60">Manage your classes and track pupil progress</p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/island"
                className="flex items-center gap-2 bg-mint-green/20 border border-mint-green/50 px-4 py-2 rounded-lg text-mint-green hover:bg-mint-green/30 transition-colours"
                data-testid="teacher-play-btn"
              >
                <Gamepad2 className="w-4 h-4" />
                <span className="font-body text-sm">Play TypeTown</span>
              </Link>
              <button
                onClick={handleSendWeeklySummary}
                className="flex items-center gap-2 bg-dark-panel border border-mid-panel px-4 py-2 rounded-lg text-zap-yellow hover:border-zap-yellow transition-colours"
                data-testid="weekly-summary-btn"
              >
                <Mail className="w-4 h-4" />
                <span className="font-body text-sm">Weekly Summary</span>
              </button>
              <button
                onClick={handleExportSENCO}
                className="flex items-center gap-2 bg-dark-panel border border-mid-panel px-4 py-2 rounded-lg text-bot-cyan hover:border-bot-cyan transition-colours"
                data-testid="export-senco-btn"
              >
                <Download className="w-4 h-4" />
                <span className="font-body text-sm">SENCO Export</span>
              </button>
              <button
                onClick={() => setShowChallenge(true)}
                className="flex items-center gap-2 bg-glitch-red/20 border border-glitch-red/50 px-4 py-2 rounded-lg text-glitch-red hover:bg-glitch-red/30 transition-colours"
                data-testid="create-challenge-btn"
              >
                <Trophy className="w-4 h-4" />
                <span className="font-body text-sm">Class Challenge</span>
              </button>
            </div>
          </div>
          
          {/* School Code Banner */}
          {school?.code && (
            <div className="mb-8 bg-gradient-to-r from-bot-cyan/20 to-zap-yellow/20 border border-bot-cyan/40 rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="school-code-banner">
              <div>
                <div className="font-mono text-xs text-bot-cyan mb-1">YOUR SCHOOL CODE</div>
                <div className="font-body text-sm text-screen-white/70">Share this code with your pupils so they can log in and play.</div>
              </div>
              <div className="flex items-center gap-3">
                <code className="bg-deep-space border border-bot-cyan/40 px-5 py-2 rounded-lg font-display text-2xl tracking-widest text-zap-yellow" data-testid="school-code-value">
                  {school.code}
                </code>
                <button
                  onClick={copySchoolCode}
                  className="p-3 bg-bot-cyan/20 rounded-lg text-bot-cyan hover:bg-bot-cyan/30 transition-colors"
                  data-testid="copy-school-code-btn"
                  title="Copy code"
                >
                  {copiedCode ? <Check className="w-5 h-5 text-mint-green" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>
          )}

          {/* New teacher onboarding guide */}
          {!loading && classes.length === 0 && (
            <div className="mb-8 bg-dark-panel border-2 border-zap-yellow/40 rounded-xl p-6" data-testid="teacher-onboarding-guide">
              <h2 className="font-display text-xl text-screen-white mb-1">Welcome! Let's get your class set up</h2>
              <p className="font-body text-sm text-screen-white/60 mb-5">Three quick steps to get your pupils playing.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex flex-col gap-2 bg-deep-space rounded-lg p-4 border border-mid-panel">
                  <div className="w-7 h-7 rounded-full bg-zap-yellow text-deep-space font-display flex items-center justify-center text-sm">1</div>
                  <div className="font-body text-sm text-screen-white font-semibold">Create your first class</div>
                  <div className="font-body text-xs text-screen-white/50">Give it a name and year group.</div>
                  <button
                    onClick={() => setShowAddClass(true)}
                    className="mt-1 flex items-center gap-1 text-bot-cyan hover:text-bot-cyan/80 font-body text-xs"
                    data-testid="onboarding-create-class-btn"
                  >
                    <Plus className="w-3 h-3" /> Create class
                  </button>
                </div>
                <div className="flex flex-col gap-2 bg-deep-space rounded-lg p-4 border border-mid-panel">
                  <div className="w-7 h-7 rounded-full bg-mid-panel text-screen-white font-display flex items-center justify-center text-sm">2</div>
                  <div className="font-body text-sm text-screen-white font-semibold">Add your pupils</div>
                  <div className="font-body text-xs text-screen-white/50">Open a class and add pupil logins.</div>
                </div>
                <div className="flex flex-col gap-2 bg-deep-space rounded-lg p-4 border border-mid-panel">
                  <div className="w-7 h-7 rounded-full bg-mid-panel text-screen-white font-display flex items-center justify-center text-sm">3</div>
                  <div className="font-body text-sm text-screen-white font-semibold">Share the code</div>
                  <div className="font-body text-xs text-screen-white/50">Give pupils your School Code above to log in.</div>
                </div>
              </div>
            </div>
          )}


          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-dark-panel border border-mid-panel rounded-xl p-6" data-testid="stat-classes">
              <Building2 className="w-8 h-8 text-bot-cyan mb-3" />
              <div className="font-display text-3xl text-screen-white">{classes.length}</div>
              <div className="font-mono text-xs text-screen-white/50">CLASSES</div>
            </div>
            <div className="bg-dark-panel border border-mid-panel rounded-xl p-6" data-testid="stat-pupils">
              <Users className="w-8 h-8 text-zap-yellow mb-3" />
              <div className="font-display text-3xl text-screen-white">{dashboard?.total_pupils || 0}</div>
              <div className="font-mono text-xs text-screen-white/50">TOTAL PUPILS</div>
            </div>
            <div className="bg-dark-panel border border-mid-panel rounded-xl p-6" data-testid="stat-sessions">
              <BarChart3 className="w-8 h-8 text-bot-cyan mb-3" />
              <div className="font-display text-3xl text-screen-white">{dashboard?.weekly_stats?.total_sessions || 0}</div>
              <div className="font-mono text-xs text-screen-white/50">SESSIONS THIS WEEK</div>
            </div>
            <div className="bg-dark-panel border border-mid-panel rounded-xl p-6" data-testid="stat-accuracy">
              <Target className="w-8 h-8 text-zap-yellow mb-3" />
              <div className="font-display text-3xl text-screen-white">
                {dashboard?.weekly_stats?.avg_accuracy?.toFixed(1) || 0}%
              </div>
              <div className="font-mono text-xs text-screen-white/50">AVG ACCURACY</div>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Classes List */}
            <div className="bg-dark-panel border border-mid-panel rounded-xl p-6" data-testid="classes-panel">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl text-screen-white">Classes</h2>
                <button
                  onClick={() => setShowAddClass(true)}
                  className="p-2 bg-bot-cyan/20 rounded-lg text-bot-cyan hover:bg-bot-cyan/30 transition-colors"
                  data-testid="add-class-btn"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-2">
                {classes.length === 0 ? (
                  <p className="text-screen-white/50 font-body text-sm text-center py-4">
                    No classes yet. Create one to get started!
                  </p>
                ) : (
                  classes.map((cls) => (
                    <div
                      key={cls.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedClass?.id === cls.id
                          ? 'bg-bot-cyan/20 border-bot-cyan'
                          : 'bg-deep-space border-mid-panel hover:border-bot-cyan/50'
                      }`}
                      onClick={() => fetchClassPupils(cls.id)}
                      data-testid={`class-${cls.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-body font-semibold text-screen-white">{cls.name}</div>
                          <div className="font-mono text-xs text-screen-white/50">
                            Year {cls.year_group} · {cls.pupil_count || 0} pupils
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteClass(cls.id); }}
                            className="p-1 text-glitch-red/50 hover:text-glitch-red"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <ChevronRight className="w-4 h-4 text-screen-white/30" />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Pupils List */}
            <div className="md:col-span-2 bg-dark-panel border border-mid-panel rounded-xl p-6" data-testid="pupils-panel">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-xl text-screen-white">
                  {selectedClass ? `${selectedClass.name} Pupils` : 'Select a Class'}
                </h2>
                {selectedClass && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePrintLogins}
                      disabled={pupils.length === 0}
                      className="flex items-center gap-2 px-3 py-2 bg-bot-cyan/20 rounded-lg text-bot-cyan hover:bg-bot-cyan/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="print-logins-btn"
                    >
                      <Download className="w-4 h-4" />
                      <span className="font-body text-sm">Print Logins</span>
                    </button>
                    <button
                      onClick={() => { setCreatedPupil(null); setShowAddPupil(true); }}
                      className="flex items-center gap-2 px-3 py-2 bg-zap-yellow/20 rounded-lg text-zap-yellow hover:bg-zap-yellow/30 transition-colors"
                      data-testid="add-pupil-btn"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="font-body text-sm">Add Pupil</span>
                    </button>
                  </div>
                )}
              </div>
              
              {!selectedClass ? (
                <div className="text-center py-12 text-screen-white/50">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="font-body">Select a class to view pupils</p>
                </div>
              ) : pupils.length === 0 ? (
                <div className="text-center py-12 text-screen-white/50">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="font-body">No pupils in this class yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-mid-panel">
                        <th className="text-left font-mono text-xs text-screen-white/50 pb-3">PUPIL</th>
                        <th className="text-left font-mono text-xs text-screen-white/50 pb-3">LOGIN</th>
                        <th className="text-left font-mono text-xs text-screen-white/50 pb-3">STAGE</th>
                        <th className="text-left font-mono text-xs text-screen-white/50 pb-3">ACCURACY</th>
                        <th className="text-left font-mono text-xs text-screen-white/50 pb-3">SESSIONS</th>
                        <th className="text-right font-mono text-xs text-screen-white/50 pb-3">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pupils.map((pupil) => (
                        <tr key={pupil.id} className="border-b border-mid-panel/50" data-testid={`pupil-${pupil.id}`}>
                          <td className="py-3">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-deep-space font-bold text-sm flex-shrink-0"
                                style={{ backgroundColor: pupil.pixel_color || '#00FFD0' }}
                              >
                                {(pupil.real_name || pupil.username).charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="font-body text-screen-white" data-testid={`pupil-realname-${pupil.id}`}>{pupil.real_name || pupil.username}</div>
                                <div className="font-mono text-xs text-screen-white/40">{pupil.username}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs text-zap-yellow" data-testid={`pupil-password-${pupil.id}`}>
                                {revealedPw[pupil.id] ? (pupil.plain_password || 'n/a') : '••••••'}
                              </span>
                              <button
                                onClick={() => setRevealedPw((prev) => ({ ...prev, [pupil.id]: !prev[pupil.id] }))}
                                className="p-1 text-screen-white/40 hover:text-bot-cyan"
                                title={revealedPw[pupil.id] ? 'Hide password' : 'Show password'}
                                data-testid={`reveal-password-${pupil.id}`}
                              >
                                {revealedPw[pupil.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => handleEditCodename(pupil)}
                                className="p-1 text-screen-white/40 hover:text-zap-yellow"
                                title="Change codename"
                                data-testid={`edit-codename-${pupil.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleResetPassword(pupil.id)}
                                className="p-1 text-screen-white/40 hover:text-glitch-red"
                                title="Reset password"
                                data-testid={`reset-password-${pupil.id}`}
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                          <td className="py-3">
                            <select
                              value={pupil.stage}
                              onChange={(e) => handleSetLevel(pupil.id, parseInt(e.target.value))}
                              className="bg-deep-space border border-mid-panel text-bot-cyan font-mono text-sm rounded-lg px-2 py-1 focus:border-bot-cyan outline-none cursor-pointer"
                              data-testid={`pupil-stage-${pupil.id}`}
                            >
                              {stageNames.map((n, i) => (
                                <option key={i} value={i + 1}>{n}</option>
                              ))}
                            </select>
                          </td>
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-deep-space rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    pupil.recent_accuracy >= 85 ? 'bg-bot-cyan' :
                                    pupil.recent_accuracy >= 70 ? 'bg-zap-yellow' : 'bg-glitch-red'
                                  }`}
                                  style={{ width: `${pupil.recent_accuracy}%` }}
                                />
                              </div>
                              <span className={`font-mono text-sm ${
                                pupil.recent_accuracy >= 85 ? 'text-bot-cyan' :
                                pupil.recent_accuracy >= 70 ? 'text-zap-yellow' : 'text-glitch-red'
                              }`}>
                                {pupil.recent_accuracy?.toFixed(0) || 0}%
                              </span>
                            </div>
                          </td>
                          <td className="py-3">
                            <span className="font-mono text-sm text-screen-white/70">{pupil.session_count || 0}</span>
                          </td>
                          <td className="py-3 text-right">
                            <button
                              onClick={() => handleDeletePupil(pupil.id)}
                              className="p-2 text-glitch-red/50 hover:text-glitch-red"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          
          {/* Active Challenges */}
          {dashboard?.active_challenges?.length > 0 && (
            <div className="mt-8 bg-dark-panel border border-mid-panel rounded-xl p-6" data-testid="challenges-panel">
              <h2 className="font-display text-xl text-screen-white mb-4">Active Class Challenges</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {dashboard.active_challenges.map((challenge) => (
                  <div key={challenge.id} className="bg-deep-space border border-glitch-red/30 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <Trophy className="w-6 h-6 text-glitch-red" />
                      <span className="font-mono text-xs text-screen-white/50">ACTIVE</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-center flex-1">
                        <div className="font-display text-lg text-screen-white">{challenge.class_a_name}</div>
                        <div className="font-display text-2xl text-bot-cyan">{challenge.class_a_accuracy || 0}%</div>
                      </div>
                      <div className="font-display text-xl text-glitch-red px-4">VS</div>
                      <div className="text-center flex-1">
                        <div className="font-display text-lg text-screen-white">{challenge.class_b_name}</div>
                        <div className="font-display text-2xl text-bot-cyan">{challenge.class_b_accuracy || 0}%</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      
      {/* Add Class Modal */}
      {showAddClass && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-panel border-2 border-mid-panel rounded-xl p-6 w-full max-w-md" data-testid="add-class-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl text-screen-white">Add New Class</h3>
              <button onClick={() => setShowAddClass(false)} className="text-screen-white/50 hover:text-screen-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateClass} className="space-y-4">
              <div>
                <label className="block font-body text-sm text-screen-white mb-2">Class Name</label>
                <input
                  type="text"
                  required
                  value={newClass.name}
                  onChange={(e) => setNewClass({...newClass, name: e.target.value})}
                  className="w-full bg-deep-space border-2 border-mid-panel text-screen-white rounded-lg p-3 focus:border-bot-cyan outline-none"
                  placeholder="e.g. Class 4B"
                  data-testid="class-name-input"
                />
              </div>
              <div>
                <label className="block font-body text-sm text-screen-white mb-2">Year Group</label>
                <select
                  value={newClass.year_group}
                  onChange={(e) => setNewClass({...newClass, year_group: parseInt(e.target.value)})}
                  className="w-full bg-deep-space border-2 border-mid-panel text-screen-white rounded-lg p-3 focus:border-bot-cyan outline-none"
                  data-testid="year-group-select"
                >
                  {[1, 2, 3, 4, 5, 6].map(y => (
                    <option key={y} value={y}>Year {y}</option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                className="w-full font-display uppercase tracking-wider bg-bot-cyan text-deep-space px-6 py-3 rounded-sm hover:bg-bot-cyan/90 transition-colors"
                data-testid="create-class-submit"
              >
                Create Class
              </button>
            </form>
          </div>
        </div>
      )}
      
      {/* Add Pupil Modal */}
      {showAddPupil && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-panel border-2 border-mid-panel rounded-xl p-6 w-full max-w-md" data-testid="add-pupil-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl text-screen-white">Add Pupil to {selectedClass?.name}</h3>
              <button onClick={() => { setShowAddPupil(false); setCreatedPupil(null); }} className="text-screen-white/50 hover:text-screen-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            {createdPupil ? (
              <div className="space-y-4" data-testid="created-pupil-credentials">
                <p className="font-body text-sm text-mint-green">Pupil added! Share these login details:</p>
                <div className="bg-deep-space border-2 border-mint-green/40 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between"><span className="font-body text-sm text-screen-white/60">Real name</span><span className="font-body text-sm text-screen-white">{createdPupil.real_name}</span></div>
                  <div className="flex justify-between"><span className="font-body text-sm text-screen-white/60">Username</span><span className="font-mono text-sm text-zap-yellow">{createdPupil.username}</span></div>
                  <div className="flex justify-between"><span className="font-body text-sm text-screen-white/60">Password</span><span className="font-mono text-sm text-zap-yellow">{createdPupil.password}</span></div>
                </div>
                <p className="font-body text-xs text-screen-white/50">Pupils log in with the School Code above, plus this username and password.</p>
                <div className="flex gap-3">
                  <button onClick={() => setCreatedPupil(null)} className="flex-1 font-display uppercase tracking-wider bg-bot-cyan text-deep-space px-4 py-3 rounded-sm hover:bg-bot-cyan/90 transition-colors" data-testid="add-another-pupil-btn">Add Another</button>
                  <button onClick={() => { setCreatedPupil(null); setShowAddPupil(false); }} className="flex-1 font-display uppercase tracking-wider bg-mid-panel text-screen-white px-4 py-3 rounded-sm hover:bg-mid-panel/70 transition-colors" data-testid="done-pupil-btn">Done</button>
                </div>
              </div>
            ) : (
            <form onSubmit={handleAddPupil} className="space-y-4">
              <div>
                <label className="block font-body text-sm text-screen-white mb-2">Pupil's Real Name <span className="text-screen-white/40">(only you can see this)</span></label>
                <input
                  type="text"
                  required
                  value={newPupil.real_name}
                  onChange={(e) => setNewPupil({...newPupil, real_name: e.target.value})}
                  className="w-full bg-deep-space border-2 border-mid-panel text-screen-white rounded-lg p-3 focus:border-zap-yellow outline-none"
                  placeholder="e.g. Emma Watson"
                  data-testid="pupil-real-name-input"
                />
              </div>
              <div>
                <label className="block font-body text-sm text-screen-white mb-2">Codename <span className="text-screen-white/40">(optional)</span></label>
                <input
                  type="text"
                  value={newPupil.codename}
                  onChange={(e) => setNewPupil({...newPupil, codename: e.target.value})}
                  className="w-full bg-deep-space border-2 border-mid-panel text-screen-white rounded-lg p-3 focus:border-zap-yellow outline-none"
                  placeholder="Leave blank to auto-generate"
                  data-testid="pupil-codename-input"
                />
                <p className="mt-2 text-xs text-screen-white/50">We'll generate a fun codename and password. You can always view and change them later.</p>
              </div>
              <button
                type="submit"
                className="w-full font-display uppercase tracking-wider bg-zap-yellow text-deep-space px-6 py-3 rounded-sm hover:bg-zap-yellow/90 transition-colors"
                data-testid="add-pupil-submit"
              >
                Add Pupil
              </button>
            </form>
            )}
          </div>
        </div>
      )}
      
      {/* Class Challenge Modal */}
      {showChallenge && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-panel border-2 border-glitch-red/50 rounded-xl p-6 w-full max-w-md" data-testid="challenge-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl text-glitch-red">Create Class Challenge</h3>
              <button onClick={() => setShowChallenge(false)} className="text-screen-white/50 hover:text-screen-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateChallenge} className="space-y-4">
              <div>
                <label className="block font-body text-sm text-screen-white mb-2">Class A</label>
                <select
                  required
                  value={challengeData.class_a_id}
                  onChange={(e) => setChallengeData({...challengeData, class_a_id: e.target.value})}
                  className="w-full bg-deep-space border-2 border-mid-panel text-screen-white rounded-lg p-3 focus:border-glitch-red outline-none"
                  data-testid="challenge-class-a"
                >
                  <option value="">Select class</option>
                  {classes.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="text-center font-display text-xl text-glitch-red">VS</div>
              <div>
                <label className="block font-body text-sm text-screen-white mb-2">Class B</label>
                <select
                  required
                  value={challengeData.class_b_id}
                  onChange={(e) => setChallengeData({...challengeData, class_b_id: e.target.value})}
                  className="w-full bg-deep-space border-2 border-mid-panel text-screen-white rounded-lg p-3 focus:border-glitch-red outline-none"
                  data-testid="challenge-class-b"
                >
                  <option value="">Select class</option>
                  {classes.filter(c => c.id !== challengeData.class_a_id).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                className="w-full font-display uppercase tracking-wider bg-glitch-red text-white px-6 py-3 rounded-sm hover:bg-glitch-red/90 transition-colors"
                data-testid="create-challenge-submit"
              >
                Start Challenge
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherDashboardPage;
